import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PaymentSystem, PAYMENT_SYSTEM_LABELS } from '../../../types/payment';
import {
  PaymentGatewayService,
  SyncPreviewItem,
  ExternalCustomer,
  ExternalCustomerSyncGroup,
  ExternalPayment,
  ExternalSubscriptionWithPayments,
} from '../../../services/PaymentGatewayService';
import { StudentService } from '../../../services/StudentService';
import { requestEdgeFunction } from '../../../services/edgeFunctionClient';
import { formatCurrency, formatDate, matchesSearchText } from '../../../utils';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import Button from '../../../components/common/Button/Button';
import Loading from '../../../components/common/Loading/Loading';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import DataTable from '../../../components/common/DataTable/DataTable';
import Icon from '../../../components/common/Icon/Icon';
import FormInput from '../../../components/common/FormInput/FormInput';
import Checkbox from '../../../components/common/Checkbox/Checkbox';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import styles from './GatewaySyncTab.module.css';

type SyncStatus = 'unlinked' | 'missing-payments' | 'up-to-date' | 'not-found';
type FilterKey = 'all' | 'unlinked' | 'missing-payments';
type Phase = 'idle' | 'loading' | 'results' | 'syncing' | 'done';

interface SyncCustomerGroup extends ExternalCustomerSyncGroup {
  existingPaymentAccountId: number | null;
}

interface SyncRow {
  studentId: number;
  studentName: string;
  email: string;
  customerGroups: SyncCustomerGroup[];
  existingPaymentIds: Set<string>;
  status: SyncStatus;
  missingCount: number;
  totalPaymentCount: number;
  selectedPaymentKeys: Set<string>;
}

interface ExistingPaymentAccount {
  paymentAccountId: number;
  externalCustomerId: string;
}

interface SyncError {
  studentId: number;
  externalCustomerId: string | null;
  message: string;
}

interface SyncResult {
  syncedStudents: number;
  syncedCustomers: number;
  syncedCharges: number;
  errors: number;
  errorDetails: SyncError[];
}

interface SyncBatchProgress {
  currentBatch: number;
  totalBatches: number;
  processed: number;
  remaining: number;
}

interface DisableAsaasNotificationsProgress {
  processed: number;
  updated: number;
  skipped: number;
  failed: number;
  total: number;
}

interface SyncCommitItem {
  studentId: number;
  externalCustomerId: string;
  customerName: string | null;
  customerEmail: string | null;
  existingPaymentAccountId: number | null;
  payments: Array<{
    id: string;
    value: number;
    status: string;
    dueDate: string | null;
    paymentDate: string | null;
    raw: Record<string, unknown>;
  }>;
}

interface RowPaymentEntry {
  selectionKey: string;
  payment: ExternalPayment;
}

interface RenderSubscriptionEntry {
  customer: ExternalCustomer;
  existingPaymentAccountId: number | null;
  subscription: ExternalSubscriptionWithPayments;
}

interface RenderStandaloneEntry {
  customer: ExternalCustomer;
  existingPaymentAccountId: number | null;
  payments: ExternalPayment[];
}

const SUPPORTED_PROVIDERS: PaymentSystem[] = ['asaas', 'stripe', 'paypal'];
const SYNC_BATCH_SIZE = 90;
const BATCH_DELAY_MS = 2000;
const MAX_PREVIEW_REQUESTS_PER_RUN = 20;

const GRID_TEMPLATE = '72px 1.5fr 1.5fr 170px 110px 120px';
const HEADERS = ['', 'Aluna', 'E-mail', 'Status', 'Cobranças', 'ID Gateway'];

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'Todos',
  unlinked: 'Sem conta',
  'missing-payments': 'Histórico incompleto',
};
const GATEWAY_FILTER_SCHEMA = {
  provider: queryFilter.enum<PaymentSystem>('asaas', SUPPORTED_PROVIDERS, { param: 'syncProvider' }),
  filter: queryFilter.enum<FilterKey>('all', ['all', 'unlinked', 'missing-payments'], { param: 'syncFilter' }),
  listSearch: queryFilter.string('', { param: 'syncListSearch' }),
  selectedStudentIds: queryFilter.array<string>([], { param: 'syncSelectedStudentIds' }),
};

const STATUS_MAP: Record<SyncStatus, { label: string; className: string }> = {
  unlinked: { label: 'Sem conta', className: styles.badgeUnlinked },
  'missing-payments': { label: 'Histórico incompleto', className: styles.badgeMissing },
  'up-to-date': { label: 'Em dia', className: styles.badgeUpToDate },
  'not-found': { label: 'Não encontrada', className: styles.badgeNotFound },
};

const wait = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const getDateSortValue = (value: string | null | undefined): number => {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getPaymentSortValue = (payment: ExternalPayment): number => Math.max(
  getDateSortValue(payment.paymentDate),
  getDateSortValue(payment.dueDate),
);

const getSubscriptionSortValue = (subscription: ExternalSubscriptionWithPayments): number => Math.max(
  getDateSortValue(subscription.dateCreated),
  ...subscription.payments.map(getPaymentSortValue),
  0,
);

const sortPaymentsNewestFirst = (payments: ExternalPayment[]): ExternalPayment[] => [...payments]
  .sort((left, right) => getPaymentSortValue(right) - getPaymentSortValue(left));

const getPaymentSelectionKey = (customerId: string, paymentId: string): string => `${customerId}::${paymentId}`;

const getSyncItemKey = (studentId: number, externalCustomerId: string | null): string => `${studentId}::${externalCustomerId ?? ''}`;

const getRowPaymentEntries = (row: SyncRow): RowPaymentEntry[] => row.customerGroups.flatMap((group) => ([
  ...group.subscriptions.flatMap((subscription) => subscription.payments.map((payment) => ({
    selectionKey: getPaymentSelectionKey(group.customer.id, payment.id),
    payment,
  }))),
  ...group.standalonePayments.map((payment) => ({
    selectionKey: getPaymentSelectionKey(group.customer.id, payment.id),
    payment,
  })),
]));

const getMissingRowPayments = (row: SyncRow): RowPaymentEntry[] => getRowPaymentEntries(row)
  .filter((entry) => !row.existingPaymentIds.has(entry.payment.id));

const getRowMissingSelectionKeys = (row: SyncRow): string[] => getMissingRowPayments(row)
  .map((entry) => entry.selectionKey);

const getGroupMissingCount = (group: SyncCustomerGroup, existingPaymentIds: Set<string>): number => ([
  ...group.subscriptions.flatMap((subscription) => subscription.payments),
  ...group.standalonePayments,
]).filter((payment) => !existingPaymentIds.has(payment.id)).length;

const resolveRowStatus = (row: SyncRow): SyncStatus => {
  if (row.customerGroups.length === 0) {
    return 'not-found';
  }

  if (getMissingRowPayments(row).length === 0) {
    return 'up-to-date';
  }

  const hasUnlinkedMissingPayments = row.customerGroups.some(
    (group) => group.existingPaymentAccountId == null && getGroupMissingCount(group, row.existingPaymentIds) > 0,
  );

  return hasUnlinkedMissingPayments ? 'unlinked' : 'missing-payments';
};

const areAllMissingPaymentsSelected = (row: SyncRow): boolean => {
  const missingSelectionKeys = getRowMissingSelectionKeys(row);
  return missingSelectionKeys.length > 0 && missingSelectionKeys.every((key) => row.selectedPaymentKeys.has(key));
};

const getSortedSubscriptionEntries = (row: SyncRow): RenderSubscriptionEntry[] => row.customerGroups
  .flatMap((group) => group.subscriptions.map((subscription) => ({
    customer: group.customer,
    existingPaymentAccountId: group.existingPaymentAccountId,
    subscription,
  })))
  .sort((left, right) => getSubscriptionSortValue(right.subscription) - getSubscriptionSortValue(left.subscription));

const getSortedStandaloneEntries = (row: SyncRow): RenderStandaloneEntry[] => row.customerGroups
  .filter((group) => group.standalonePayments.length > 0)
  .map((group) => ({
    customer: group.customer,
    existingPaymentAccountId: group.existingPaymentAccountId,
    payments: sortPaymentsNewestFirst(group.standalonePayments),
  }))
  .sort((left, right) => {
    const leftValue = Math.max(...left.payments.map(getPaymentSortValue), 0);
    const rightValue = Math.max(...right.payments.map(getPaymentSortValue), 0);
    return rightValue - leftValue;
  });

const buildGatewayIdLabel = (customerGroups: SyncCustomerGroup[]): string => {
  if (customerGroups.length === 0) {
    return '—';
  }

  if (customerGroups.length === 1) {
    return customerGroups[0].customer.id;
  }

  return `${customerGroups.length} contas`;
};

const buildSyncRow = (
  student: { id: number; full_name: string; email: string },
  preview: SyncPreviewItem | undefined,
  linkedAccountsByStudentId: Map<number, Map<string, ExistingPaymentAccount>>,
  globalExistingIds: Set<string>,
): SyncRow => {
  const linkedAccounts = linkedAccountsByStudentId.get(student.id) ?? new Map<string, ExistingPaymentAccount>();

  const customerGroups: SyncCustomerGroup[] = (preview?.customers ?? []).map((group) => ({
    ...group,
    subscriptions: [...group.subscriptions]
      .map((subscription) => ({
        ...subscription,
        payments: sortPaymentsNewestFirst(subscription.payments),
      }))
      .sort((left, right) => getSubscriptionSortValue(right) - getSubscriptionSortValue(left)),
    standalonePayments: sortPaymentsNewestFirst(group.standalonePayments),
    existingPaymentAccountId: linkedAccounts.get(group.customer.id)?.paymentAccountId ?? null,
  }));

  const allPayments = customerGroups.flatMap((group) => ([
    ...group.subscriptions.flatMap((subscription) => subscription.payments),
    ...group.standalonePayments,
  ]));

  const row: SyncRow = {
    studentId: student.id,
    studentName: student.full_name,
    email: student.email,
    customerGroups,
    existingPaymentIds: new Set(
      allPayments.filter((payment) => globalExistingIds.has(payment.id)).map((payment) => payment.id),
    ),
    status: 'not-found',
    missingCount: 0,
    totalPaymentCount: allPayments.length,
    selectedPaymentKeys: new Set<string>(),
  };

  const missingEntries = getMissingRowPayments(row);
  row.missingCount = missingEntries.length;
  row.selectedPaymentKeys = new Set(missingEntries.map((entry) => entry.selectionKey));
  row.status = resolveRowStatus(row);

  return row;
};

const GatewaySyncTab: React.FC = () => {
  const { filters, setFilter, setFilters } = useQueryFilters(GATEWAY_FILTER_SCHEMA);
  const {
    provider,
    filter,
    listSearch,
    selectedStudentIds,
  } = filters;
  const [phase, setPhase] = useState<Phase>('idle');
  const [rows, setRows] = useState<SyncRow[]>([]);
  const [studentsWithEmails, setStudentsWithEmails] = useState<Array<{ id: number; full_name: string; email: string }>>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentLoadError, setStudentLoadError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncBatchProgress, setSyncBatchProgress] = useState<SyncBatchProgress | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [confirmPending, setConfirmPending] = useState(false);
  const [disableNotificationsPending, setDisableNotificationsPending] = useState(false);
  const [disableNotificationsRunning, setDisableNotificationsRunning] = useState(false);
  const [disableNotificationsProgress, setDisableNotificationsProgress] = useState<DisableAsaasNotificationsProgress | null>(null);
  const [disableNotificationsError, setDisableNotificationsError] = useState<string | null>(null);
  const selectedStudentIdsKey = selectedStudentIds.join(',');

  const studentOptions = useMemo(() => studentsWithEmails.map((student) => ({
    value: String(student.id),
    label: `${student.full_name} • ${student.email}`,
    searchText: `${student.full_name} ${student.email}`,
  })), [studentsWithEmails]);

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      setStudentsLoading(true);
      setStudentLoadError(null);

      try {
        const data = await StudentService.findAllWithEmails();

        if (!isMounted) {
          return;
        }

        setStudentsWithEmails(data);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setStudentLoadError(error instanceof Error ? error.message : 'Erro ao carregar alunas para seleção manual.');
      } finally {
        if (isMounted) {
          setStudentsLoading(false);
        }
      }
    };

    void loadStudents();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setConfirmPending(false);
    setPhase('idle');
    setRows([]);
    setSyncResult(null);
    setLoadError(null);
    setSelectionError(null);
  }, [provider, selectedStudentIdsKey]);

  const toggleExpand = (studentId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleFetch = useCallback(async () => {
    setPhase('loading');
    setLoadError(null);
    setSelectionError(null);
    setSyncResult(null);
    setRows([]);
    setFilters({ filter: 'all', listSearch: '' }, { resetPage: false });

    try {
      const allStudents = await StudentService.findAllWithEmails();
      const selectedIds = new Set(selectedStudentIds);
      const students = selectedIds.size > 0
        ? allStudents.filter((student) => selectedIds.has(String(student.id)))
        : allStudents;

      if (students.length === 0) {
        setRows([]);
        setPhase('results');
        return;
      }

      const linkedJunctions = await PaymentGatewayService.listLinkedPaymentAccountsByProvider(
        provider,
        students.map((student) => student.id),
      );

      const linkedAccountsByStudentId = new Map<number, Map<string, ExistingPaymentAccount>>();
      for (const junction of linkedJunctions) {
        const paymentAccount = junction.payment_accounts;
        const externalCustomerId = paymentAccount?.external_customer_id ?? '';

        if (!externalCustomerId) {
          continue;
        }

        const studentAccounts = linkedAccountsByStudentId.get(junction.student_id) ?? new Map<string, ExistingPaymentAccount>();
        studentAccounts.set(externalCustomerId, {
          paymentAccountId: junction.payment_account_id,
          externalCustomerId,
        });
        linkedAccountsByStudentId.set(junction.student_id, studentAccounts);
      }

      const previewInput = students.map((student) => ({ studentId: student.id, email: student.email }));
      const previewChunks = chunkArray(previewInput, SYNC_BATCH_SIZE);

      if (previewChunks.length > MAX_PREVIEW_REQUESTS_PER_RUN) {
        throw new Error(
          `A busca no gateway está limitada a ${MAX_PREVIEW_REQUESTS_PER_RUN} requisições por execução (${MAX_PREVIEW_REQUESTS_PER_RUN * SYNC_BATCH_SIZE} alunas). Selecione menos alunas e tente novamente.`,
        );
      }

      const previews: SyncPreviewItem[] = [];
      for (let index = 0; index < previewChunks.length; index += 1) {
        const batchPreviews = await PaymentGatewayService.syncPreview(provider, previewChunks[index]);
        previews.push(...batchPreviews);

        if (index < previewChunks.length - 1) {
          await wait(BATCH_DELAY_MS);
        }
      }

      const previewByStudentId = new Map(previews.map((preview) => [preview.studentId, preview]));
      const allGatewayPaymentIds = previews.flatMap((preview) => preview.customers.flatMap((group) => ([
        ...group.subscriptions.flatMap((subscription) => subscription.payments.map((payment) => payment.id)),
        ...group.standalonePayments.map((payment) => payment.id),
      ])));

      const globalExistingIds = new Set<string>();
      if (allGatewayPaymentIds.length > 0) {
        const chunkSize = 400;
        for (let index = 0; index < allGatewayPaymentIds.length; index += chunkSize) {
          const currentChunk = allGatewayPaymentIds.slice(index, index + chunkSize);
          const response = await requestEdgeFunction<{ data?: string[] }>('payment-gateway', {
            method: 'POST',
            body: {
              action: 'list-existing-payment-history-ids',
              external_payment_ids: currentChunk,
            },
          });

          for (const externalPaymentId of response.data ?? []) {
            globalExistingIds.add(externalPaymentId);
          }
        }
      }

      setRows(students.map((student) => buildSyncRow(
        student,
        previewByStudentId.get(student.id),
        linkedAccountsByStudentId,
        globalExistingIds,
      )));
      setPhase('results');
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Erro desconhecido ao buscar dados.');
      setPhase('idle');
    }
  }, [provider, selectedStudentIds, setFilters]);

  const toggleRow = useCallback((studentId: number) => {
    setConfirmPending(false);
    setRows((prev) => prev.map((row) => {
      if (row.studentId !== studentId) {
        return row;
      }

      if (areAllMissingPaymentsSelected(row)) {
        return { ...row, selectedPaymentKeys: new Set<string>() };
      }

      return { ...row, selectedPaymentKeys: new Set(getRowMissingSelectionKeys(row)) };
    }));
    setSelectionError(null);
  }, []);

  const togglePaymentSelection = useCallback((studentId: number, customerId: string, paymentId: string) => {
    const selectionKey = getPaymentSelectionKey(customerId, paymentId);

    setConfirmPending(false);
    setRows((prev) => prev.map((row) => {
      if (row.studentId !== studentId || row.existingPaymentIds.has(paymentId)) {
        return row;
      }

      const nextSelectedPaymentKeys = new Set(row.selectedPaymentKeys);
      if (nextSelectedPaymentKeys.has(selectionKey)) {
        nextSelectedPaymentKeys.delete(selectionKey);
      } else {
        nextSelectedPaymentKeys.add(selectionKey);
      }

      return { ...row, selectedPaymentKeys: nextSelectedPaymentKeys };
    }));
    setSelectionError(null);
  }, []);

  const normalizedListSearch = listSearch.trim();

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filter === 'all') {
      return row.status === 'unlinked' || row.status === 'missing-payments';
    }

    return row.status === filter;
  }).filter((row) => {
    if (!normalizedListSearch) {
      return true;
    }

    return matchesSearchText(row.studentName, normalizedListSearch)
      || matchesSearchText(row.email, normalizedListSearch);
  }), [filter, normalizedListSearch, rows]);

  const allVisible = visibleRows.length > 0 && visibleRows.every((row) => areAllMissingPaymentsSelected(row));

  const toggleAll = useCallback(() => {
    const shouldSelect = !allVisible;
    const visibleIds = new Set(visibleRows.map((row) => row.studentId));

    setRows((prev) => prev.map((row) => {
      if (!visibleIds.has(row.studentId)) {
        return row;
      }

      if (!shouldSelect || row.status === 'not-found' || row.status === 'up-to-date') {
        return { ...row, selectedPaymentKeys: new Set<string>() };
      }

      return { ...row, selectedPaymentKeys: new Set(getRowMissingSelectionKeys(row)) };
    }));
    setSelectionError(null);
  }, [allVisible, visibleRows]);

  const handleStudentSelectionChange = useCallback((values: string[]) => {
    setConfirmPending(false);
    setPhase('idle');
    setRows([]);
    setSyncResult(null);
    setLoadError(null);
    setFilter('selectedStudentIds', values, { resetPage: false });
    setSelectionError(null);
  }, [setFilter]);

  const handleFilterChange = useCallback((newFilter: FilterKey) => {
    setFilter('filter', newFilter, { resetPage: false });
  }, [setFilter]);

  const handleSync = useCallback(async () => {
    const items: SyncCommitItem[] = rows.flatMap((row) => row.customerGroups.flatMap((group) => {
      const selectedPayments = [
        ...group.subscriptions.flatMap((subscription) => subscription.payments),
        ...group.standalonePayments,
      ].filter((payment) => {
        const selectionKey = getPaymentSelectionKey(group.customer.id, payment.id);
        return row.selectedPaymentKeys.has(selectionKey) && !row.existingPaymentIds.has(payment.id);
      });

      if (selectedPayments.length === 0) {
        return [];
      }

      return [{
        studentId: row.studentId,
        externalCustomerId: group.customer.id,
        customerName: group.customer.name ?? null,
        customerEmail: group.customer.email ?? null,
        existingPaymentAccountId: group.existingPaymentAccountId,
        payments: selectedPayments.map((payment) => ({
          id: payment.id,
          value: payment.value,
          status: payment.status,
          dueDate: payment.dueDate,
          paymentDate: payment.paymentDate,
          raw: payment.raw,
        })),
      }];
    }));

    if (items.length === 0) {
      return;
    }

    setPhase('syncing');
    setSyncProgress(0);
    setSyncBatchProgress(null);
    setSelectionError(null);

    try {
      const batches = chunkArray(items, SYNC_BATCH_SIZE);
      const totalItems = items.length;
      let processed = 0;
      let syncedCustomers = 0;
      let syncedCharges = 0;
      const syncedStudents = new Set<number>();
      const syncedItemsByStudentId = new Map<number, SyncCommitItem[]>();
      let allErrorDetails: SyncError[] = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];
        const currentBatch = batchIndex + 1;

        setSyncBatchProgress({
          currentBatch,
          totalBatches: batches.length,
          processed,
          remaining: totalItems - processed,
        });

        const result = await PaymentGatewayService.syncCommit(provider, batch);
        const batchErrors = result.errorDetails ?? [];
        const failedItemKeys = new Set(batchErrors.map((error) => getSyncItemKey(error.studentId, error.externalCustomerId)));
        const successfulBatchItems = batch.filter(
          (item) => !failedItemKeys.has(getSyncItemKey(item.studentId, item.externalCustomerId)),
        );

        for (const item of successfulBatchItems) {
          syncedCustomers += 1;
          syncedCharges += item.payments.length;
          syncedStudents.add(item.studentId);

          const currentItems = syncedItemsByStudentId.get(item.studentId) ?? [];
          currentItems.push(item);
          syncedItemsByStudentId.set(item.studentId, currentItems);
        }

        allErrorDetails = [...allErrorDetails, ...batchErrors];
        processed += batch.length;

        setSyncBatchProgress({
          currentBatch,
          totalBatches: batches.length,
          processed,
          remaining: Math.max(totalItems - processed, 0),
        });
        setSyncProgress(Math.round((processed / totalItems) * 100));

        if (batchIndex < batches.length - 1) {
          await wait(BATCH_DELAY_MS);
        }
      }

      setSyncResult({
        syncedStudents: syncedStudents.size,
        syncedCustomers,
        syncedCharges,
        errors: allErrorDetails.length,
        errorDetails: allErrorDetails,
      });

      setRows((prev) => prev.map((row) => {
        const syncedItems = syncedItemsByStudentId.get(row.studentId) ?? [];
        if (syncedItems.length === 0) {
          return row;
        }

        const nextExistingPaymentIds = new Set(row.existingPaymentIds);
        const nextSelectedPaymentKeys = new Set(row.selectedPaymentKeys);

        syncedItems.forEach((item) => {
          item.payments.forEach((payment) => {
            nextExistingPaymentIds.add(payment.id);
            nextSelectedPaymentKeys.delete(getPaymentSelectionKey(item.externalCustomerId, payment.id));
          });
        });

        const nextRow: SyncRow = {
          ...row,
          existingPaymentIds: nextExistingPaymentIds,
          selectedPaymentKeys: nextSelectedPaymentKeys,
          missingCount: row.missingCount,
          status: row.status,
        };

        nextRow.missingCount = getMissingRowPayments(nextRow).length;
        nextRow.status = resolveRowStatus(nextRow);
        return nextRow;
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
      setSyncResult({
        syncedStudents: 0,
        syncedCustomers: 0,
        syncedCharges: 0,
        errors: items.length,
        errorDetails: [{ studentId: 0, externalCustomerId: null, message }],
      });
    } finally {
      setSyncBatchProgress(null);
    }

    setSyncProgress(100);
    setPhase('done');
  }, [provider, rows]);

  const handleDisableAsaasNotifications = useCallback(async () => {
    const selectedIds = selectedStudentIds
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0);

    if (selectedIds.length === 0) {
      setDisableNotificationsPending(false);
      setDisableNotificationsError('Selecione ao menos uma aluna para desativar os e-mails do Asaas.');
      return;
    }

    setDisableNotificationsPending(false);
    setDisableNotificationsRunning(true);
    setDisableNotificationsError(null);
    setDisableNotificationsProgress({ processed: 0, updated: 0, skipped: 0, failed: 0, total: 0 });

    try {
      let offset = 0;
      let hasMore = true;
      const totals: DisableAsaasNotificationsProgress = {
        processed: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
        total: 0,
      };

      while (hasMore) {
        const result = await PaymentGatewayService.disableAsaasNotifications(selectedIds, offset, 50);
        totals.processed += result.processed;
        totals.updated += result.updated;
        totals.skipped += result.skipped;
        totals.failed += result.failed;
        totals.total = result.total;
        setDisableNotificationsProgress({ ...totals });

        hasMore = result.hasMore;
        offset = result.nextOffset;

        if (hasMore) {
          await wait(BATCH_DELAY_MS);
        }
      }
    } catch (error) {
      setDisableNotificationsError(
        error instanceof Error ? error.message : 'Não foi possível desativar as notificações do Asaas.',
      );
    } finally {
      setDisableNotificationsRunning(false);
    }
  }, [selectedStudentIds]);

  const countUnlinked = rows.filter((row) => row.status === 'unlinked').length;
  const countMissing = rows.filter((row) => row.status === 'missing-payments').length;
  const countUpToDate = rows.filter((row) => row.status === 'up-to-date').length;
  const countNotFound = rows.filter((row) => row.status === 'not-found').length;
  const selectedChargeCount = rows.reduce((sum, row) => sum + row.selectedPaymentKeys.size, 0);
  const selectedStudentCount = rows.filter((row) => row.selectedPaymentKeys.size > 0).length;
  const manualSelectionCount = selectedStudentIds.length;

  return (
    <div className={styles.root}>
      <PageHeader
        title="Sincronização de Gateway"
        subtitle="Importe clientes e histórico de cobranças de Asaas, Stripe ou PayPal para o portal."
      />

      {studentLoadError && <p className={styles.error}>{studentLoadError}</p>}
      {selectionError && <p className={styles.selectionError}>{selectionError}</p>}

      <div className={styles.pickerSection}>
        <div className={styles.studentPickerRow}>
          <div className={styles.studentPickerField}>
            <p className={styles.pickerLabel}>Alunas</p>
            <div className={styles.studentPickerControls}>
              <MultiSelectFilter
                values={selectedStudentIds}
                onChange={handleStudentSelectionChange}
                options={studentOptions}
                placeholder={studentsLoading ? 'Carregando...' : 'Todas as alunas'}
                searchPlaceholder="Pesquisar aluna"
                disabled={studentsLoading || phase === 'loading' || phase === 'syncing'}
                hideChips
                noResultsText="Nenhuma aluna encontrada"
                clearButtonLabel="Limpar seleção"
              />
              {manualSelectionCount > 0 && (
                <ClearFiltersButton onClick={() => handleStudentSelectionChange([])} />
              )}
            </div>
            <p className={styles.studentPickerHint}>
              {studentsLoading
                ? 'Carregando alunas...'
                : manualSelectionCount > 0
                  ? `${manualSelectionCount} ${manualSelectionCount === 1 ? 'aluna selecionada' : 'alunas selecionadas'}`
                  : `${studentOptions.length} ${studentOptions.length === 1 ? 'aluna disponível' : 'alunas disponíveis'} · sem filtro`}
            </p>
          </div>
        </div>

        <p className={styles.pickerLabel}>Gateway</p>
        <div className={styles.gatewayCards}>
          {SUPPORTED_PROVIDERS.map((currentProvider) => (
            <button
              key={currentProvider}
              type="button"
              className={`${styles.gatewayCard} ${provider === currentProvider ? styles.selected : ''}`}
              onClick={() => {
                setFilter('provider', currentProvider, { resetPage: false });
                setPhase('idle');
                setRows([]);
                setLoadError(null);
                setSyncResult(null);
              }}
              disabled={phase === 'loading' || phase === 'syncing'}
            >
              <span className={styles.gatewayCardIcon}>
                <Icon name="payment" size={20} />
              </span>
              {PAYMENT_SYSTEM_LABELS[currentProvider]}
            </button>
          ))}
        </div>

        <div className={styles.pickerActions}>
          <Button
            variant="primary"
            size="medium"
            icon="cloud_sync"
            onClick={handleFetch}
            disabled={phase === 'loading' || phase === 'syncing' || studentsLoading}
          >
            {phase === 'loading' ? 'Buscando...' : 'Buscar no gateway'}
          </Button>

          {phase !== 'idle' && phase !== 'loading' && (
            <Button
              variant="ghost"
              size="medium"
              onClick={() => {
                setPhase('idle');
                setRows([]);
                setSyncResult(null);
                setLoadError(null);
              }}
            >
              Limpar
            </Button>
          )}

        </div>

        <div className={styles.limitNotice}>
          <Icon name="info" size={16} />
          <span>
            A busca processa no máximo <strong>{SYNC_BATCH_SIZE} alunas por lote</strong> e a sincronização até <strong>{SYNC_BATCH_SIZE} itens por lote</strong>, com pausa de 2 segundos entre lotes.
            A busca também está limitada a <strong>{MAX_PREVIEW_REQUESTS_PER_RUN} requisições por execução</strong>.
          </span>
        </div>

        {loadError && <p className={styles.error}>{loadError}</p>}
      </div>

      {provider === 'asaas' && (
        <div className={styles.notificationsSection}>
          <p className={styles.pickerLabel}>E-mails automáticos do Asaas</p>
          <p className={styles.helperText}>
            Desliga o envio de cobrança por e-mail, SMS e WhatsApp do Asaas só para as alunas
            selecionadas acima que já têm conta Asaas. A cobrança continua sendo gerada; só para
            de ser disparada para a aluna. Novos cadastros já nascem com isso desligado.
          </p>
          <div className={styles.pickerActions}>
            <Button
              variant="primary"
              size="medium"
              icon="notifications_off"
              onClick={() => setDisableNotificationsPending(true)}
              disabled={
                disableNotificationsRunning
                || phase === 'loading'
                || phase === 'syncing'
                || manualSelectionCount === 0
              }
            >
              {disableNotificationsRunning
                ? 'Desativando e-mails...'
                : manualSelectionCount > 0
                  ? `Desativar e-mails (${manualSelectionCount})`
                  : 'Desativar e-mails das selecionadas'}
            </Button>
          </div>
          {disableNotificationsError && <p className={styles.error}>{disableNotificationsError}</p>}
          {disableNotificationsProgress && (
            <p className={styles.helperText}>
              {disableNotificationsRunning
                ? 'Desativando e-mails automáticos do Asaas...'
                : 'E-mails automáticos do Asaas atualizados.'}
              {' '}
              {disableNotificationsProgress.total > 0
                ? `${disableNotificationsProgress.processed}/${disableNotificationsProgress.total}`
                : disableNotificationsProgress.processed}
              {' contas · '}
              Atualizadas: {disableNotificationsProgress.updated}
              {' · '}
              Ignoradas: {disableNotificationsProgress.skipped}
              {' · '}
              Falhas: {disableNotificationsProgress.failed}
            </p>
          )}
        </div>
      )}

      <ConfirmModal
        open={disableNotificationsPending}
        variant="warning"
        title="Desativar e-mails do Asaas"
        message={`Isso desativa o envio automático de cobrança por e-mail, SMS e WhatsApp do Asaas para ${manualSelectionCount} aluna${manualSelectionCount === 1 ? '' : 's'} selecionada${manualSelectionCount === 1 ? '' : 's'}. A cobrança continua sendo gerada; só para de ser disparada para a aluna.`}
        confirmLabel="Desativar e-mails"
        cancelLabel="Cancelar"
        onConfirm={handleDisableAsaasNotifications}
        onCancel={() => setDisableNotificationsPending(false)}
      />

      {phase === 'loading' && <Loading />}

      {(phase === 'results' || phase === 'done') && (
        <>
          <div className={styles.summaryRow}>
            <div className={`${styles.summaryCard} ${styles.cardWarning}`}>
              <span className={styles.summaryValue}>{countUnlinked}</span>
              <p className={styles.summaryLabel}>Sem conta vinculada</p>
            </div>
            <div className={`${styles.summaryCard} ${styles.cardInfo}`}>
              <span className={styles.summaryValue}>{countMissing}</span>
              <p className={styles.summaryLabel}>Histórico incompleto</p>
            </div>
            <div className={`${styles.summaryCard} ${styles.cardSuccess}`}>
              <span className={styles.summaryValue}>{countUpToDate}</span>
              <p className={styles.summaryLabel}>Em dia</p>
            </div>
          </div>

          {syncResult && (
            <div className={syncResult.errors > 0 ? styles.syncResultBanner : styles.successBanner}>
              <span>
                Sincronização concluída: {syncResult.syncedCharges} cobranças importadas em {syncResult.syncedStudents} alunas
                {syncResult.syncedCustomers > 0 ? ` (${syncResult.syncedCustomers} conta${syncResult.syncedCustomers === 1 ? '' : 's'} do gateway).` : '.'}
                {syncResult.errors > 0 ? ` ${syncResult.errors} item${syncResult.errors === 1 ? '' : 's'} com erro.` : ''}
              </span>
              {syncResult.errorDetails.length > 0 && (
                <ul className={styles.syncErrorList}>
                  {syncResult.errorDetails.map((error, index) => (
                    <li key={`${error.studentId}-${error.externalCustomerId ?? 'sem-conta'}-${index}`}>
                      <strong>Aluna #{error.studentId}</strong>
                      {error.externalCustomerId ? ` • Conta ${error.externalCustomerId}` : ''}
                      : {error.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {countUnlinked + countMissing === 0 ? (
            <EmptyState
              message="Todas as alunas com e-mail cadastrado já estão sincronizadas com o gateway."
              icon="check_circle"
            />
          ) : (
            <>
              <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                  <div className={styles.filterTabs}>
                    {(['all', 'unlinked', 'missing-payments'] as FilterKey[]).map((currentFilter) => (
                      <button
                        key={currentFilter}
                        type="button"
                        className={`${styles.filterTab} ${filter === currentFilter ? styles.activeTab : ''}`}
                        onClick={() => handleFilterChange(currentFilter)}
                      >
                        {FILTER_LABELS[currentFilter]}
                      </button>
                    ))}
                  </div>

                  <div className={styles.searchField}>
                    <FormInput
                      id="gateway-sync-list-search"
                      label="Pesquisar"
                      value={listSearch}
                      onChange={(event) => setFilter('listSearch', event.target.value, { resetPage: false })}
                      placeholder="Pesquisar"
                    />
                  </div>
                </div>

                <div className={styles.toolbarRight}>
                  <span className={styles.toolbarSelectionSummary}>
                    {selectedChargeCount} cobranças selecionadas em {selectedStudentCount} alunas
                  </span>
                  {selectedChargeCount > 0 && !confirmPending && (
                    <Button
                      variant="primary"
                      size="medium"
                      icon="sync"
                      onClick={() => setConfirmPending(true)}
                    >
                      Sincronizar {selectedChargeCount} cobrança{selectedChargeCount === 1 ? '' : 's'}
                    </Button>
                  )}
                </div>
              </div>

              <ConfirmModal
                open={confirmPending}
                variant="warning"
                title="Confirmar sincronização"
                message={`Confirma a sincronização de ${selectedChargeCount} cobrança${selectedChargeCount === 1 ? '' : 's'} em ${selectedStudentCount} aluna${selectedStudentCount === 1 ? '' : 's'}? O processo será em lotes sequenciais de até ${SYNC_BATCH_SIZE} itens, com pausa de 2 segundos entre lotes. Novas contas do gateway serão vinculadas quando necessário e apenas as cobranças selecionadas serão importadas.`}
                confirmLabel="Confirmar"
                cancelLabel="Cancelar"
                onConfirm={() => {
                  setConfirmPending(false);
                  handleSync();
                }}
                onCancel={() => setConfirmPending(false)}
              />

              {visibleRows.length === 0 ? (
                <EmptyState message="Nenhuma aluna nesta categoria." icon="filter_list" />
              ) : (
                <DataTable headers={getHeaders(allVisible, toggleAll)} gridTemplate={GRID_TEMPLATE} borderedRows boldHeaders>
                  {visibleRows.map((row) => {
                    const statusInfo = STATUS_MAP[row.status];

                    return (
                      <React.Fragment key={row.studentId}>
                        <DataTable.Row>
                          <div className={styles.checkboxCell}>
                            <Checkbox
                              checked={areAllMissingPaymentsSelected(row)}
                              onPress={() => toggleRow(row.studentId)}
                              aria-label={`Selecionar ${row.studentName}`}
                              compact
                              className={styles.checkboxControl}
                            />
                          </div>
                          <a
                            href={`/alunas/${row.studentId}/editar`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.studentLink}
                          >
                            {row.studentName}
                          </a>
                          <span>{row.email}</span>
                          <span className={`${styles.badge} ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                          <span className={styles.missingCount}>
                            {row.totalPaymentCount > 0 ? (
                              <button
                                type="button"
                                className={styles.expandBtn}
                                onClick={() => toggleExpand(row.studentId)}
                                aria-label="Ver cobranças"
                              >
                                {row.missingCount}/{row.totalPaymentCount}
                                <Icon
                                  name={expandedRows.has(row.studentId) ? 'expand_less' : 'expand_more'}
                                  size={14}
                                />
                              </button>
                            ) : '—'}
                          </span>
                          <span className={styles.mono}>{buildGatewayIdLabel(row.customerGroups)}</span>
                        </DataTable.Row>

                        {expandedRows.has(row.studentId) && row.totalPaymentCount > 0 && (
                          <div className={styles.expandedPanel}>
                            <p className={styles.expandedSummary}>
                              {row.missingCount} cobrança{row.missingCount === 1 ? '' : 's'} pendente{row.missingCount === 1 ? '' : 's'} de {row.totalPaymentCount} encontrada{row.totalPaymentCount === 1 ? '' : 's'} no gateway.
                            </p>

                            {getSortedSubscriptionEntries(row).map((entry) => (
                              <div key={`${entry.customer.id}-${entry.subscription.external_subscription_id}`} className={styles.subscriptionCard}>
                                <div className={styles.subscriptionHeader}>
                                  <div>
                                    <p className={styles.subscriptionTitle}>
                                      {entry.subscription.description ?? 'Assinatura sem descrição'}
                                    </p>
                                    <div className={styles.subscriptionMeta}>
                                      <span>
                                        {entry.subscription.dateCreated ? `Criada em ${formatDate(entry.subscription.dateCreated)}` : 'Data não informada'}
                                      </span>
                                      <span>{entry.subscription.status || 'Status não informado'}</span>
                                      <span>{entry.customer.email ?? row.email}</span>
                                      <span>{entry.existingPaymentAccountId ? 'Conta já vinculada' : 'Nova conta no portal'}</span>
                                    </div>
                                  </div>
                                  <span className={styles.mono}>{entry.subscription.external_subscription_id}</span>
                                </div>

                                {entry.subscription.payments.length > 0 ? (
                                  <>
                                    <div className={styles.expandedHeader}>
                                      <span />
                                      <span>Vencimento</span>
                                      <span>Valor</span>
                                      <span>Status</span>
                                      <span>Descrição</span>
                                      <span>ID Gateway</span>
                                    </div>
                                    {entry.subscription.payments.map((payment) => {
                                      const isExisting = row.existingPaymentIds.has(payment.id);
                                      const selectionKey = getPaymentSelectionKey(entry.customer.id, payment.id);
                                      const isSelected = row.selectedPaymentKeys.has(selectionKey);

                                      return (
                                        <div
                                          key={`${entry.customer.id}-${payment.id}`}
                                          className={`${styles.expandedRow} ${isExisting ? styles.expandedRowExisting : ''}`}
                                        >
                                          <div className={styles.paymentSelectionCell}>
                                            <Checkbox
                                              checked={isExisting || isSelected}
                                              onPress={() => togglePaymentSelection(row.studentId, entry.customer.id, payment.id)}
                                              disabled={isExisting}
                                              compact
                                              variant="classic"
                                              aria-label={`Selecionar cobrança ${payment.id}`}
                                              className={styles.checkboxControl}
                                            />
                                          </div>
                                          <span>{payment.dueDate ? formatDate(payment.dueDate) : '—'}</span>
                                          <span>{formatCurrency(payment.value)}</span>
                                          <span className={styles.mono}>{isExisting ? `${payment.status} • NO PORTAL` : payment.status}</span>
                                          <span>{payment.description ?? entry.subscription.description ?? '—'}</span>
                                          <span className={styles.mono}>{payment.id}</span>
                                        </div>
                                      );
                                    })}
                                  </>
                                ) : (
                                  <p className={styles.emptyGroupText}>Nenhuma cobrança encontrada para esta assinatura.</p>
                                )}
                              </div>
                            ))}

                            {getSortedStandaloneEntries(row).map((entry) => (
                              <div key={`${entry.customer.id}-standalone`} className={styles.subscriptionCard}>
                                <div className={styles.subscriptionHeader}>
                                  <div>
                                    <p className={styles.subscriptionTitle}>Cobranças avulsas</p>
                                    <div className={styles.subscriptionMeta}>
                                      <span>{entry.customer.email ?? row.email}</span>
                                      <span>{entry.existingPaymentAccountId ? 'Conta já vinculada' : 'Nova conta no portal'}</span>
                                      <span>{entry.payments.length} cobrança{entry.payments.length === 1 ? '' : 's'}</span>
                                    </div>
                                  </div>
                                  <span className={styles.mono}>{entry.customer.id}</span>
                                </div>

                                <div className={styles.expandedHeader}>
                                  <span />
                                  <span>Vencimento</span>
                                  <span>Valor</span>
                                  <span>Status</span>
                                  <span>Descrição</span>
                                  <span>ID Gateway</span>
                                </div>

                                {entry.payments.map((payment) => {
                                  const isExisting = row.existingPaymentIds.has(payment.id);
                                  const selectionKey = getPaymentSelectionKey(entry.customer.id, payment.id);
                                  const isSelected = row.selectedPaymentKeys.has(selectionKey);

                                  return (
                                    <div
                                      key={`${entry.customer.id}-${payment.id}`}
                                      className={`${styles.expandedRow} ${isExisting ? styles.expandedRowExisting : ''}`}
                                    >
                                      <div className={styles.paymentSelectionCell}>
                                        <Checkbox
                                          checked={isExisting || isSelected}
                                          onPress={() => togglePaymentSelection(row.studentId, entry.customer.id, payment.id)}
                                          disabled={isExisting}
                                          compact
                                          variant="classic"
                                          aria-label={`Selecionar cobrança ${payment.id}`}
                                          className={styles.checkboxControl}
                                        />
                                      </div>
                                      <span>{payment.dueDate ? formatDate(payment.dueDate) : '—'}</span>
                                      <span>{formatCurrency(payment.value)}</span>
                                      <span className={styles.mono}>{isExisting ? `${payment.status} • NO PORTAL` : payment.status}</span>
                                      <span>{payment.description ?? 'Cobrança avulsa'}</span>
                                      <span className={styles.mono}>{payment.id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </DataTable>
              )}

              {countNotFound > 0 && (
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
                  {countNotFound} {countNotFound === 1 ? 'aluna não foi encontrada' : 'alunas não foram encontradas'} no {PAYMENT_SYSTEM_LABELS[provider]}.
                </p>
              )}
            </>
          )}
        </>
      )}

      {phase === 'syncing' && (
        <div className={styles.syncProgress}>
          <Icon name="sync" size={32} />
          <p>Sincronizando... {syncProgress}%</p>
          {syncBatchProgress && (
            <>
              <p>Sincronizando lote {syncBatchProgress.currentBatch}/{syncBatchProgress.totalBatches}</p>
              <p>{syncBatchProgress.processed} itens processados</p>
              <p>{syncBatchProgress.remaining} restantes</p>
            </>
          )}
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${syncProgress}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};

function getHeaders(allSelected: boolean, onToggleAll: () => void): React.ReactNode[] {
  return [
    <div key="check" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Checkbox
        checked={allSelected}
        onPress={onToggleAll}
        aria-label="Selecionar todas"
        compact
        className={styles.checkboxControl}
      />
    </div>,
    ...HEADERS.slice(1),
  ];
}

export default GatewaySyncTab;
